/**
 * Firestore access for `skidaCrmLeads`.
 *
 * Per the target architecture, this module is the *only* place leads touch
 * Firebase. UI components import hooks, hooks import this — no component ever
 * imports from `firebase/*` directly.
 */
import {
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { CRM_COLLECTIONS } from '@/shared/firebase/collections'
import {
  EMPTY_ENRICHMENT,
  EMPTY_QUALIFICATION,
  type Contact,
  type FollowUpTask,
  type Lead,
  type LeadEnrichment,
  type LeadFlag,
  type ProLink,
  type Qualification,
} from '@/types/crm'
import { toIsoOrNull } from '@/shared/util/dates'
import { companyDomain, leadSlug, normaliseHandle, normaliseWebsite } from '../normalise'

/** Most severe first, so the banner order never depends on write order. */
const FLAG_ORDER: Record<LeadFlag['level'], number> = { critical: 0, warning: 1, info: 2 }

function mapFlags(value: unknown): LeadFlag[] {
  if (!Array.isArray(value)) return []
  return value
    .filter((f): f is DocumentData => Boolean(f) && typeof f.text === 'string')
    .map<LeadFlag>((f) => ({
      // Anything unrecognised degrades to the least alarming level rather than
      // being dropped — a flag that fails to render is worse than a mild one.
      level: f.level === 'critical' || f.level === 'warning' ? f.level : 'info',
      label: typeof f.label === 'string' ? f.label : '',
      text: f.text,
    }))
    .sort((a, b) => FLAG_ORDER[a.level] - FLAG_ORDER[b.level])
}

/**
 * Read the harvested enrichment defensively.
 *
 * A harvest run writes a partial map — it fills what it could source and leaves
 * the rest alone — so every field falls back to the empty value rather than
 * being trusted to exist. A missing score must read as "not scored yet", never
 * as zero: a zero would sort a company nobody has looked at below one that was
 * genuinely assessed and found weak.
 */
function mapEnrichment(raw: unknown): LeadEnrichment {
  if (!raw || typeof raw !== 'object') return EMPTY_ENRICHMENT
  const d = raw as DocumentData
  const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)
  const str = (v: unknown): string => (typeof v === 'string' ? v : '')
  const list = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []

  return {
    ...EMPTY_ENRICHMENT,
    leadScore: num(d.leadScore),
    tier: d.tier === 'A' || d.tier === 'B' || d.tier === 'C' || d.tier === 'D' ? d.tier : null,
    confidence:
      d.confidence === 'high' || d.confidence === 'medium' || d.confidence === 'low'
        ? d.confidence
        : null,
    axisSize: num(d.axisSize),
    axisDigital: num(d.axisDigital),
    axisPower: num(d.axisPower),
    teamSize: num(d.teamSize),
    teamSizeSource: str(d.teamSizeSource),
    guidesNamed: num(d.guidesNamed),
    guidesClaimed: num(d.guidesClaimed),
    productCount: num(d.productCount),
    hiringNow: d.hiringNow === true,
    operatingYears: num(d.operatingYears),
    entityType: str(d.entityType) as LeadEnrichment['entityType'] | null,
    estAcvEur: num(d.estAcvEur),
    orgNumber: str(d.orgNumber),
    employees: str(d.employees),
    employeesYear: num(d.employeesYear),
    revenueEur: num(d.revenueEur),
    revenueYear: num(d.revenueYear),
    ceo: str(d.ceo),
    foundedYear: num(d.foundedYear),
    instagram: str(d.instagram),
    instagramFollowers: num(d.instagramFollowers),
    instagramVerified: d.instagramVerified === true,
    siteReadAt: str(d.siteReadAt),
    bookingMode: (str(d.bookingMode) || null) as LeadEnrichment['bookingMode'],
    bookingVendor: str(d.bookingVendor),
    mapTools: list(d.mapTools),
    safetyTools: list(d.safetyTools),
    waiverDigital: d.waiverDigital === true,
    ownAppOrPortal: d.ownAppOrPortal === true,
    cms: str(d.cms),
    socialLastPostDays: num(d.socialLastPostDays),
    displacementRisk: d.displacementRisk === true,
    stackComplementary: d.stackComplementary === true,
    pricePerPersonDayEur: num(d.pricePerPersonDayEur),
    priceCurrencyOriginal: str(d.priceCurrencyOriginal),
    priceSourceUrl: str(d.priceSourceUrl),
    productTier: list(d.productTier),
    luxuryMarkers: list(d.luxuryMarkers),
    certLevel: str(d.certLevel),
    clientType: list(d.clientType),
    countryCostTier: (str(d.countryCostTier) || null) as LeadEnrichment['countryCostTier'],
    snapshot: str(d.snapshot),
    hook: str(d.hook),
    contactWindow: str(d.contactWindow),
    baseTown: str(d.baseTown),
    latitude: num(d.latitude),
    longitude: num(d.longitude),
    geoStatus: (str(d.geoStatus) || null) as LeadEnrichment['geoStatus'],
    evidence: Array.isArray(d.evidence)
      ? d.evidence
          .filter((e: DocumentData) => e && typeof e.url === 'string')
          .map((e: DocumentData) => ({
            field: str(e.field),
            url: str(e.url),
            retrievedAt: str(e.retrievedAt),
          }))
      : [],
    verify: d.verify === true,
    siteBlocked: d.siteBlocked === true,
    enrichedAt: toIsoOrNull(d.enrichedAt),
    source: str(d.source),
  }
}

function mapLead(snap: QueryDocumentSnapshot<DocumentData>): Lead {
  const d = snap.data()
  return {
    // Written by scripts/geocodeAll.ts and by nothing else, so it is passed
    // through as stored — precision included, which is the half that matters.
    // Spread in, never `undefined` — see the note in `partnersRepository`.
    ...(d.geo ? { geo: d.geo as Lead['geo'] } : {}),
    id: snap.id,
    name: d.name ?? '(unnamed lead)',
    region: d.region ?? '',
    owner: d.owner ?? 'Unassigned',
    stage: d.stage ?? 'prospect',
    contacts: Array.isArray(d.contacts) ? d.contacts : [],
    nextTask: d.nextTask ?? null,
    flags: mapFlags(d.flags),
    website: d.website ?? '',
    instagram: d.instagram ?? '',
    facebook: d.facebook ?? '',
    logoUrl: d.logoUrl ?? '',
    teamSize: d.teamSize ?? '',
    prospectingNotes: d.prospectingNotes ?? '',
    contactDomains: Array.isArray(d.contactDomains) ? d.contactDomains : [],
    address: d.address ?? '',
    country: d.country ?? '',
    qualification: {
      hasVolume: d.qualification?.hasVolume === true,
      hasDigitalTools: d.qualification?.hasDigitalTools === true,
      highSpend: d.qualification?.highSpend === true,
      comment: d.qualification?.comment ?? '',
    },
    enrichment: mapEnrichment(d.enrichment),
    hasProductUsers: d.hasProductUsers === true,
    productUserCount: typeof d.productUserCount === 'number' ? d.productUserCount : 0,
    skidaProCompanyId: d.skidaProCompanyId ?? null,
    proLink:
      d.proLink && typeof d.proLink.companyId === 'string'
        ? {
            companyId: d.proLink.companyId,
            method: d.proLink.method ?? 'manual',
            by: typeof d.proLink.by === 'string' ? d.proLink.by : '',
            at: toIsoOrNull(d.proLink.at),
          }
        : null,
    // Typed by a rep; absent on every lead created before 4 Sep 2026.
    seatsExpected: typeof d.seatsExpected === 'number' ? d.seatsExpected : 0,
    seatsBought: typeof d.seatsBought === 'number' ? d.seatsBought : 0,
    seatsActive: typeof d.seatsActive === 'number' ? d.seatsActive : 0,
    guides: Array.isArray(d.guides)
      ? d.guides.map((g: DocumentData) => ({
          name: g.name ?? '',
          email: g.email ?? '',
          signedIn: Boolean(g.signedIn),
          firstLoginAt: toIsoOrNull(g.firstLoginAt),
        }))
      : [],
    // Spread in, never `undefined` — a present key with no value is what
    // Firestore refuses, and it made every partner without a geocoded point
    // unsaveable. See the note in `partnersRepository`.
    ...(toIsoOrNull(d.createdAt) ? { createdAt: toIsoOrNull(d.createdAt)! } : {}),
    ...(toIsoOrNull(d.updatedAt) ? { updatedAt: toIsoOrNull(d.updatedAt)! } : {}),
    wonAt: toIsoOrNull(d.wonAt),
  }
}

/**
 * Live subscription to every lead, newest-updated first.
 * Returns the unsubscribe function.
 */
export function subscribeToLeads(
  onData: (leads: Lead[]) => void,
  onError: (error: Error) => void,
): () => void {
  const q = query(collection(db, CRM_COLLECTIONS.leads), orderBy('name'))
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map(mapLead)),
    (err) => onError(err as Error),
  )
}

/**
 * The `wonAt` change a stage transition implies, or nothing.
 *
 * The design's sales goal counts "companies won this season", and nothing here
 * recorded *when* a company was won — a stage change wrote the stage and
 * `updatedAt`, and `updatedAt` moves every time somebody fixes a phone number.
 *
 * Three cases, and the two that are not "set it" are the ones worth having a
 * function for:
 *
 * * **Into `won`** — stamp it.
 * * **Out of `won`** — clear it. A deal marked won by mistake and corrected
 *   must stop counting, or the goal measures how many times somebody clicked
 *   Won rather than how many companies were won.
 * * **`won` → `won`** — leave it alone. Saving the profile of a company won in
 *   October must not move its win into today, which is exactly what an
 *   unconditional stamp would do the first time anyone edited an address.
 */
function wonAtPatch(from: string, to: string): { wonAt?: unknown } {
  if (to === 'won') return from === 'won' ? {} : { wonAt: serverTimestamp() }
  return from === 'won' ? { wonAt: null } : {}
}

/** Move a lead to another pipeline stage or outcome. */
export async function updateLeadStage(
  leadId: string,
  stage: string,
  /** The stage being left, so a win keeps the date it was won on. */
  fromStage: string,
): Promise<void> {
  await updateDoc(doc(db, CRM_COLLECTIONS.leads, leadId), {
    stage,
    ...wonAtPatch(fromStage, stage),
    updatedAt: serverTimestamp(),
  })
}

/** Replace the lead's next follow-up task. */
export async function updateNextTask(leadId: string, task: FollowUpTask | null): Promise<void> {
  await updateDoc(doc(db, CRM_COLLECTIONS.leads, leadId), {
    nextTask: task,
    updatedAt: serverTimestamp(),
  })
}

/** Mark the current follow-up as done. Keeps the task for the record, stamped. */
export async function completeNextTask(leadId: string, task: FollowUpTask): Promise<void> {
  await updateDoc(doc(db, CRM_COLLECTIONS.leads, leadId), {
    nextTask: { ...task, doneAt: new Date().toISOString() },
    updatedAt: serverTimestamp(),
  })
}

/** Reassign the owning sales rep. */
/**
 * Hand a lead to somebody. Both fields move together, always.
 *
 * `ownerEmail` is what `firestore.rules` matches for an external-sales caller,
 * so a write that set one and not the other would either strand a lead its owner
 * cannot see or leave it visible to the person who used to have it. They are one
 * fact and this is the only place that writes it.
 */
export async function updateLeadOwner(
  leadId: string,
  owner: string,
  ownerEmail: string,
): Promise<void> {
  await updateDoc(doc(db, CRM_COLLECTIONS.leads, leadId), {
    owner,
    ownerEmail: ownerEmail.trim().toLowerCase(),
    updatedAt: serverTimestamp(),
  })
}

/** Company-profile fields editable from the "Edit lead info" modal. */
export interface LeadProfilePatch {
  name: string
  region: string
  website: string
  instagram: string
  facebook: string
  stage: string
  address: string
  country: string
  /** Seats this deal is for. See `Lead.seatsExpected`. */
  seatsExpected: number
}

/**
 * Save the lead profile.
 *
 * `contactDomains` is recomputed from the website and every contact email, so
 * a corrected website immediately restores the product-event attribution the
 * Cloud Functions depend on.
 */
export async function updateLeadProfile(
  lead: Lead,
  patch: LeadProfilePatch,
): Promise<void> {
  const website = normaliseWebsite(patch.website)
  await updateDoc(doc(db, CRM_COLLECTIONS.leads, lead.id), {
    name: patch.name.trim(),
    region: patch.region.trim(),
    website,
    instagram: normaliseHandle(patch.instagram),
    // Same normaliser as Instagram: it drops the host and keeps the first path
    // segment, so a pasted profile URL and a bare handle both land as a handle.
    facebook: normaliseHandle(patch.facebook),
    stage: patch.stage,
    ...wonAtPatch(lead.stage, patch.stage),
    address: patch.address.trim(),
    country: patch.country.trim(),
    // Whole seats, never negative. A typed field reaching Firestore as NaN
    // would break every sum that reads it, and `checkNoUndefinedWrites` cannot
    // catch that one.
    seatsExpected: Number.isFinite(patch.seatsExpected)
      ? Math.max(0, Math.round(patch.seatsExpected))
      : 0,
    contactDomains: deriveDomains(website, lead.contacts),
    updatedAt: serverTimestamp(),
  })
}

/**
 * Stage keys the verification step moves between.
 *
 * Step 0 of the sales process: every lead lands in `unverified`, somebody spends
 * a few seconds on it, and it goes one of two ways. Nothing is deleted —
 * `archived` keeps the decision, so the next import cannot resurrect a company
 * that was already judged and rejected.
 */
export const VERIFY_TARGET = 'prospect'
export const ARCHIVE_TARGET = 'archived'

/**
 * Banners that exist only to say "nobody has looked at this yet".
 *
 * They are written by the harvest scripts, which cannot know whether a company
 * name derived from a domain is right. Verifying is precisely the act of
 * looking — so once a human has, the banner has served its purpose and keeping
 * it would tell the next reader something untrue.
 *
 * Matched on label rather than level: a `critical` flag is never cleared here,
 * and an `info` flag a person wrote by hand is not ours to remove.
 */
const VERIFICATION_FLAGS = new Set(['Name not verified', 'Quarantined'])

/**
 * Accept a lead into the pipeline.
 *
 * Takes the whole lead rather than an id so the provenance flags can be
 * filtered without a read-then-write — the caller already has the document.
 */
export async function verifyLead(lead: Pick<Lead, 'id' | 'flags'>): Promise<void> {
  await updateDoc(doc(db, CRM_COLLECTIONS.leads, lead.id), {
    stage: VERIFY_TARGET,
    flags: lead.flags.filter((f) => !VERIFICATION_FLAGS.has(f.label)),
    verifiedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
}

/** Take a lead out of the pipeline without destroying it. */
export async function archiveLead(leadId: string): Promise<void> {
  await updateDoc(doc(db, CRM_COLLECTIONS.leads, leadId), {
    stage: ARCHIVE_TARGET,
    archivedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
}

/**
 * Quick fixes made during verification, without opening the full edit modal.
 *
 * Only the fields that decide whether a lead is workable at all. Everything
 * else can wait until somebody actually works it.
 */
export async function updateLeadIdentity(
  leadId: string,
  patch: { website: string; country: string; region: string },
): Promise<void> {
  const website = normaliseWebsite(patch.website)
  const domain = website ? companyDomain(website) : null
  await updateDoc(doc(db, CRM_COLLECTIONS.leads, leadId), {
    website,
    country: patch.country.trim(),
    region: patch.region.trim(),
    // A corrected website is the whole point of this step: without a company
    // domain the Cloud Functions cannot attribute a Skida PRO signup back here.
    ...(domain ? { contactDomains: arrayUnion(domain) } : {}),
    updatedAt: serverTimestamp(),
  })
}

/** Save the qualification scorecard. */
export async function updateQualification(
  leadId: string,
  qualification: Qualification,
): Promise<void> {
  await updateDoc(doc(db, CRM_COLLECTIONS.leads, leadId), {
    qualification,
    updatedAt: serverTimestamp(),
  })
}

function deriveDomains(website: string, contacts: Contact[]): string[] {
  const out = new Set<string>()
  for (const source of [website, ...contacts.map((c) => c.email)]) {
    const d = source ? companyDomain(source) : null
    if (d) out.add(d)
  }
  return [...out]
}

export interface NewLeadInput {
  name: string
  region: string
  owner: string
  stage: string
  contact?: Contact
}

/**
 * Create a lead. The id is derived from the company name, so creating a
 * company that already exists updates it rather than producing a duplicate —
 * `merge: true` keeps whatever the import or the Cloud Functions already put
 * there. Returns the id so the caller can select the lead.
 */
export async function createLead(input: NewLeadInput): Promise<string> {
  const id = leadSlug(input.name)
  const contacts = input.contact ? [input.contact] : []
  await setDoc(
    doc(db, CRM_COLLECTIONS.leads, id),
    {
      name: input.name.trim(),
      region: input.region.trim(),
      owner: input.owner.trim() || 'Unassigned',
      stage: input.stage,
      contacts,
      contactDomains: deriveDomains('', contacts),
      nextTask: null,
      flags: [],
      qualification: EMPTY_QUALIFICATION,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  )
  return id
}

/**
 * Replace the whole contacts array.
 *
 * Firestore has no array-element update, and the array is small, so the UI
 * sends the full list. `contactDomains` is recomputed at the same time — a
 * newly added work address is exactly the signal the Cloud Functions need.
 */
export async function saveContacts(lead: Lead, contacts: Contact[]): Promise<void> {
  await updateDoc(doc(db, CRM_COLLECTIONS.leads, lead.id), {
    contacts,
    contactDomains: deriveDomains(lead.website, contacts),
    updatedAt: serverTimestamp(),
  })
}

/** Delete a lead outright. Admin-only, enforced in the Firestore rules. */
export async function deleteLead(leadId: string): Promise<void> {
  await deleteDoc(doc(db, CRM_COLLECTIONS.leads, leadId))
}

/**
 * Link a lead to the Skida PRO company it is — or undo it with `null`.
 *
 * Writes `proLink` only, never `skidaProCompanyId`: that one belongs to the
 * Cloud Functions and the rules refuse it from a browser. Internal only — the
 * product company is read, never written.
 */
export async function setProLink(
  leadId: string,
  link: { companyId: string; method: ProLink['method']; by: string } | null,
): Promise<void> {
  await updateDoc(doc(db, CRM_COLLECTIONS.leads, leadId), {
    proLink: link ? { ...link, at: serverTimestamp() } : null,
    updatedAt: serverTimestamp(),
  })
}

/**
 * A lead made from a Skida PRO company's billing profile, already linked.
 *
 * Everything filled in is what the customer typed into the product — legal
 * name, VAT number, address — so the lead starts out with the strongest
 * identity it can have. `createLead` derives the id from the name and merges,
 * so a name that is already a lead is refused rather than merged over.
 */
export async function createLeadFromProCompany(input: {
  name: string
  owner: string
  ownerEmail: string
  stage: string
  companyId: string
  orgNumber: string
  country: string
  address: string
  email: string
}): Promise<string> {
  // `createLead` merges on a name-derived id and would reset an existing
  // lead's contacts and stage, so a name already in the CRM is refused: that
  // company is linked, not created.
  if ((await getDoc(doc(db, CRM_COLLECTIONS.leads, leadSlug(input.name)))).exists()) {
    throw new Error(`A lead called ${input.name} already exists. Link it instead.`)
  }
  const id = await createLead({ name: input.name, region: '', owner: input.owner, stage: input.stage })
  const domain = input.email ? companyDomain(input.email) : null
  await updateDoc(doc(db, CRM_COLLECTIONS.leads, id), {
    ownerEmail: input.ownerEmail,
    ...(input.country ? { country: input.country } : {}),
    ...(input.address ? { address: input.address } : {}),
    ...(input.orgNumber ? { 'enrichment.orgNumber': input.orgNumber } : {}),
    ...(domain ? { contactDomains: arrayUnion(domain) } : {}),
    proLink: { companyId: input.companyId, method: 'manual', by: input.ownerEmail, at: serverTimestamp() },
    updatedAt: serverTimestamp(),
  })
  return id
}
